import React from 'react';
import { useIntl } from 'react-intl';
import type { DirectedScript } from 'fym-shared/src/music24/directedVideoApi';
import type { MusicVideoSongMapSection } from 'fym-shared/src/music24/musicVideoApi';
import { TypedMemo } from '../../../common/types';
import { formatTime } from '../../../musicVideoEditor/timeline/timelineGeometry';
import { scriptMessages as messages } from '../../messages/scriptMessages';
import { useDirectedCastNames } from '../../useDirectedCastNames';
import { sectionAt, sectionDisplayName } from '../../shell/sectionBar';
import { clipNumbers, isClipStart } from './scriptEdits';
import { MoreIcon } from './ShotIcons';
import { ShotThumb } from './ShotThumb';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the script step; each part uses its own slice of it */
import styles from './Script.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PhoneShotListProps {
  script: DirectedScript;
  sections: readonly MusicVideoSongMapSection[];
  /** The still of each made clip by `clipKey`; a shot with none shows a placeholder tile. */
  stillOf: ReadonlyMap<string, string>;
  editedIds: ReadonlySet<string>;
  rewritingShotId: string | null;
  disabled: boolean;
  onMore: (shotId: string) => void;
}

/**
 * The script on a phone (design j3): a header for each section in the place of the strip, one
 * short row per shot, and a "…" button that opens the sheet of the shot.
 */
const PhoneShotListComponent: React.FC<PhoneShotListProps> = ({
  script,
  sections,
  stillOf,
  editedIds,
  rewritingShotId,
  disabled,
  onMore,
}) => {
  const intl = useIntl();
  const { named } = useDirectedCastNames();
  const numbers = clipNumbers(script);
  let lastSection: number | null = null;
  return (
    <div className={styles.shots}>
      {script.shots.map((shot, index) => {
        const section = sectionAt(sections, shot.startSec);
        const header =
          section !== null && section.index !== lastSection ? (
            <span className={styles.phoneHead}>
              {intl.formatMessage(messages.sectionAt, {
                section: sectionDisplayName(section.label),
                time: formatTime(section.startSec),
              })}
            </span>
          ) : null;
        lastSection = section?.index ?? lastSection;
        const clipNumber = numbers.get(shot.clipKey) ?? 1;
        const extras = [
          editedIds.has(shot.id) ? intl.formatMessage(messages.rowEdited) : null,
          isClipStart(script, shot.id) ? null : intl.formatMessage(messages.partOfClip, { number: clipNumber }),
        ].filter((extra): extra is string => extra !== null);
        const isRewriting = shot.id === rewritingShotId;
        return (
          <React.Fragment key={shot.id}>
            {header}
            <div className={styles.phoneRow} data-testid={`phone-shot-${shot.id}`}>
              <ShotThumb number={index + 1} stillUrl={stillOf.get(shot.clipKey) ?? null} compact />
              <div>
                <span className={styles.phoneTime}>
                  {[`${index + 1} · ${formatTime(shot.startSec)}–${formatTime(shot.endSec)}`, ...extras].join(' · ')}
                </span>
                <div className={styles.phoneScene}>
                  {isRewriting ? intl.formatMessage(messages.rewritingNoNote) : named(shot.scene)}
                </div>
              </div>
              {isRewriting ? (
                <span className={styles.spin} aria-hidden />
              ) : (
                <button
                  type="button"
                  className={styles.iconButton}
                  aria-label={intl.formatMessage(messages.moreAria, { number: index + 1 })}
                  disabled={disabled}
                  onClick={() => onMore(shot.id)}
                >
                  <MoreIcon />
                </button>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const PhoneShotList = TypedMemo(PhoneShotListComponent);
